import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

/**
 * AuthProvider - Centralized authentication state management
 *
 * Features:
 * - Token validation on mount
 * - Automatic token expiration handling
 * - Centralized login/logout
 * - Auth state persistence
 * - Loading states for async operations
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  /**
   * Validate token with backend
   * Called on mount and after login
   */
  const validateToken = useCallback(async () => {
    const token = localStorage.getItem('admin_token');

    if (!token) {
      setLoading(false);
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }

    try {
      // Verify token with backend
      const response = await axios.get(`${API}/admin/verify-token`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.valid) {
        setUser({
          name: response.data.name || localStorage.getItem('admin_name') || 'Admin',
          email: response.data.email,
          role: response.data.role || 'admin'
        });
        setIsAuthenticated(true);
        return true;
      } else {
        throw new Error('Invalid token');
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      // Token is invalid or expired
      logout(false); // Don't show toast on initial validation
      return false;
    } finally {
      setLoading(false);
    }
  }, [API]);

  /**
   * Login function
   */
  const login = useCallback(async (credentials) => {
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);

      // Check if 2FA is required — don't touch loading state
      if (response.data.requires_2fa) {
        return {
          success: false,
          requires_2fa: true,
          admin_id: response.data.admin_id,
          email_hint: response.data.email_hint,
          message: response.data.message
        };
      }

      const { token, name, email, role } = response.data;

      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_name', name);

      setUser({ name, email, role: role || 'admin' });
      setIsAuthenticated(true);

      toast.success('Login successful');
      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [API]);

  const verifyOtp = useCallback(async (adminId, otp) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/verify-otp`, {
        admin_id: adminId,
        otp: otp
      });

      const { token, name, email, role } = response.data;

      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_name', name);

      setUser({ name, email, role: role || 'admin' });
      setIsAuthenticated(true);

      toast.success('Login successful');
      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Verification failed.';
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [API]);

  /**
   * Logout function
   */
  const logout = useCallback((showToast = true) => {
    // Clear local storage
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_name');

    // Reset state
    setUser(null);
    setIsAuthenticated(false);

    if (showToast) {
      toast.success('Logged out successfully');
    }

    // Redirect to login
    navigate('/admin/login');
  }, [navigate]);

  /**
   * Check if token is expired (client-side check)
   * This is a backup - server should always validate
   */
  const isTokenExpired = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) return true;

    try {
      // JWT tokens have 3 parts separated by dots
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));

      // Check expiration (exp is in seconds, Date.now() is in milliseconds)
      if (decoded.exp && decoded.exp * 1000 < Date.now()) {
        return true;
      }
      return false;
    } catch (error) {
      // If we can't decode the token, consider it expired
      console.error('Error decoding token:', error);
      return true;
    }
  }, []);

  /**
   * Validate token on mount
   */
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  /**
   * Set up periodic token validation
   * Check every 5 minutes if token is still valid
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      if (isTokenExpired()) {
        toast.error('Session expired. Please login again.');
        logout(false);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated, isTokenExpired, logout]);

  /**
   * Set up axios interceptor for automatic logout on 401
   */
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && isAuthenticated) {
          toast.error('Session expired. Please login again.');
          logout(false);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [isAuthenticated, logout]);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    verifyOtp,
    logout,
    validateToken,
    isTokenExpired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use auth context
 * Usage: const { user, login, logout, isAuthenticated } = useAuth();
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
